import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  COST_DRIVERS,
  COST_DRIVER_LABELS,
  agencyRateIssues,
  canAdministerPlatform,
  costLineIssues,
  formatBasisPointsPercent,
  formatPeriod,
  isPeriodStart,
  nzDate,
  parseAmount,
  periodStartFor,
  previousPeriodStart,
  type CostDriver,
  type Money,
} from '@promanage/core';
import { formatBytes } from '@promanage/core/upload';
import { Amount, Chip, Field, Input, Panel, PanelHeader, Select } from '@promanage/ui';
import { TopBar } from '../components/TopBar';
import { LoadFailed, QueryLoading } from '../components/QueryState';
import { SettingsNav } from '../features/settings/SettingsNav';
import { StatTile } from '../features/dashboard/StatTile';
import { recentMonths } from '../features/reports/months';
import { useSession } from '../features/auth/session';
import { trpc, type RouterOutputs } from '../lib/trpc';
import { SaveButton, useUnsavedChanges } from '../components/UnsavedChanges';

function parseSearch(search: Record<string, unknown>): { month?: string } {
  return typeof search.month === 'string' && isPeriodStart(search.month)
    ? { month: search.month }
    : {};
}

export const Route = createFileRoute('/settings/unit-costs')({
  validateSearch: parseSearch,
  component: UnitCostsPage,
});

type UnitCosts = RouterOutputs['platform']['unitCosts'];

/** Suggested names, so the same provider is spelled the same way every month. */
const KNOWN_SERVICES: Record<string, CostDriver> = {
  'Fly.io': 'properties',
  Upstash: 'properties',
  'Cloudflare R2': 'storage',
  Resend: 'emails',
};

/**
 * Cost per property — ROADMAP.md's third success question, beside the second.
 *
 * ProManage's own screen, not an agency's: `platform_admin` only, and the server refuses anybody
 * else whatever is drawn here. What it shows of each agency is counts — properties, bytes, emails,
 * statement lines — never a row of their books.
 *
 * THE BILLS ARE TYPED IN, NOT ESTIMATED. What each provider charged, in NZD, as it left the bank.
 * A rate card times usage would be a second opinion about the invoice; the invoice is the fact.
 * Usage only decides how each bill is split across agencies.
 */
function UnitCostsPage() {
  const session = useSession();
  const selection = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const today = nzDate(new Date());
  // Last month by default: this month's invoices have not arrived.
  const month = selection.month ?? previousPeriodStart(periodStartFor(today));
  const months = recentMonths(periodStartFor(today), 24);
  const isAdmin = canAdministerPlatform(session?.roles ?? []);

  const query = useQuery(
    trpc.platform.unitCosts.queryOptions(
      { month },
      {
        meta: { errorTitle: "Could not work out the month's costs" },
        staleTime: 30_000,
        enabled: isAdmin,
      },
    ),
  );

  return (
    <div className="mx-auto max-w-[var(--content-max)] pb-4">
      <TopBar
        title="Unit costs"
        subtitle="What running ProManage costs, per property under management"
      />
      <SettingsNav />

      {!isAdmin ? (
        <Panel className="p-6">
          <p className="text-[0.8125rem] text-ink-muted">
            This page is for ProManage platform administrators.
          </p>
        </Panel>
      ) : (
        <div className="flex flex-col gap-4">
          <Panel>
            <div className="p-5">
              <Field label="Month" className="max-w-64">
                {(props) => (
                  <Select
                    {...props}
                    value={month}
                    onChange={(event) => {
                      void navigate({ search: { month: event.target.value }, replace: true });
                    }}
                  >
                    {months.map((option) => (
                      <option key={option} value={option}>
                        {formatPeriod(option)}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
            </div>
          </Panel>

          {query.isError ? (
            <LoadFailed retry={() => void query.refetch()} retrying={query.isFetching} />
          ) : query.isPending ? (
            <QueryLoading label="Reading bills and usage..." />
          ) : (
            <UnitCostReport data={query.data} month={month} />
          )}
        </div>
      )}
    </div>
  );
}

function UnitCostReport({ data, month }: { data: UnitCosts; month: string }) {
  const perProperty = data.perPropertyCents === null ? null : BigInt(data.perPropertyCents);
  const revenuePerProperty =
    data.revenuePerPropertyCents === null ? null : BigInt(data.revenuePerPropertyCents);
  const marginPerProperty =
    data.marginPerPropertyCents === null ? null : BigInt(data.marginPerPropertyCents);

  return (
    <>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile
          label="Margin per property"
          value={marginPerProperty ?? '—'}
          /*
           * The figure the pricing decision turns on, so it leads. Green when the book pays for
           * itself, `warn` when it does not — never red, which is kept for error and destruction.
           */
          tone={
            marginPerProperty === null ? 'neutral' : marginPerProperty > 0n ? 'positive' : 'warn'
          }
          detail={
            marginPerProperty === null
              ? 'No properties under management this month'
              : `${formatBasisPointsPercent(data.marginBp)} of revenue${
                  data.unpriced > 0
                    ? ` · ${String(data.unpriced)} ${
                        data.unpriced === 1 ? 'agency' : 'agencies'
                      } not priced`
                    : ''
                }`
          }
        />
        <StatTile
          label="Charged this month"
          value={BigInt(data.totalRevenueCents)}
          detail={
            revenuePerProperty === null ? (
              'Ex-GST'
            ) : (
              <>
                Ex-GST · <Amount value={revenuePerProperty} tone="neutral" /> per property
              </>
            )
          }
        />
        <StatTile
          label="Cost to run"
          value={BigInt(data.totalCents)}
          detail={
            perProperty === null ? (
              `${String(data.bills.filter((bill) => bill.inForce).length)} providers, in NZD`
            ) : (
              <>
                <Amount value={perProperty} tone="neutral" /> per property, over{' '}
                {String(data.properties)}
              </>
            )
          }
        />
        <StatTile
          label="Matched automatically"
          value={formatBasisPointsPercent(data.matchRate.autoRateBp)}
          tone={
            data.matchRate.meetsTarget === null
              ? 'neutral'
              : data.matchRate.meetsTarget
                ? 'positive'
                : 'warn'
          }
          detail={`Every agency's statement lines · target ${formatBasisPointsPercent(
            data.matchRate.targetBp,
          )}`}
        />
      </div>

      {data.fellBack.length > 0 || data.unattributed.length > 0 ? (
        <Panel className="border-l-4 border-l-warn p-5">
          <ul className="flex flex-col gap-1 text-[0.8125rem] leading-relaxed">
            {data.fellBack.length > 0 ? (
              <li>
                <span className="font-bold">{data.fellBack.join(', ')}</span> had no usage to split
                by this month, so {data.fellBack.length === 1 ? 'it was' : 'they were'} split by
                properties instead.
              </li>
            ) : null}
            {data.unattributed.length > 0 ? (
              <li>
                <span className="font-bold">
                  {data.unattributed.map((line) => line.service).join(', ')}
                </span>{' '}
                could not be split across agencies — there were no properties under management. It
                still counts in the total.
              </li>
            ) : null}
          </ul>
        </Panel>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-3">
        <RecordBill month={month} />
        <Bills data={data} />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <SetRate month={month} data={data} />
        <Agencies data={data} />
      </div>
    </>
  );
}

function RecordBill({ month }: { month: string }) {
  const form = useUnsavedChanges();
  const client = useQueryClient();
  const [service, setService] = useState('');
  const [driver, setDriver] = useState<CostDriver>('properties');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');

  let amountCents: Money | null = null;
  let amountIssue: string | null = null;
  try {
    amountCents = amount.trim() === '' ? null : parseAmount(amount);
  } catch {
    amountIssue = 'Enter the amount in dollars and cents';
  }
  const issues = amountCents === null ? [] : costLineIssues({ service, amountCents });
  const ready =
    service.trim() !== '' && amountCents !== null && amountIssue === null && issues.length === 0;

  const record = useMutation(
    trpc.platform.recordCost.mutationOptions({
      meta: { errorTitle: 'The bill was not recorded' },
      onSuccess: async () => {
        form.saved();
        setService('');
        setAmount('');
        setNote('');
        await client.invalidateQueries({ queryKey: trpc.platform.unitCosts.queryKey() });
      },
    }),
  );

  return (
    <Panel>
      <PanelHeader
        title="Record a bill"
        subtitle={`What a provider charged for ${formatPeriod(month)}, as it left the bank.`}
      />
      <form
        onChange={form.touch}
        className="flex flex-col gap-3 px-5 pb-5"
        onSubmit={(event) => {
          event.preventDefault();
          if (!ready || amountCents === null) return;
          record.mutate({
            month,
            service: service.trim(),
            driver,
            amountCents: amountCents.toString(),
            note: note.trim() === '' ? null : note.trim(),
          });
        }}
      >
        <Field
          label="Service"
          hint="Recording the same service again replaces its bill for the month."
        >
          {(props) => (
            <>
              <Input
                {...props}
                list="known-services"
                value={service}
                maxLength={60}
                onChange={(event) => {
                  setService(event.target.value);
                  const known = KNOWN_SERVICES[event.target.value];
                  if (known) setDriver(known);
                }}
              />
              <datalist id="known-services">
                {Object.keys(KNOWN_SERVICES).map((name) => (
                  <option key={name} value={name} />
                ))}
              </datalist>
            </>
          )}
        </Field>
        <Field label="Split across agencies by">
          {(props) => (
            <Select
              {...props}
              value={driver}
              onChange={(event) => setDriver(event.target.value as CostDriver)}
            >
              {COST_DRIVERS.map((option) => (
                <option key={option} value={option}>
                  {COST_DRIVER_LABELS[option]}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field
          label="Amount (NZD)"
          hint="Zero takes a bill recorded in error back."
          error={amountIssue ?? issues[0]}
        >
          {(props) => (
            <Input
              {...props}
              inputMode="decimal"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
            />
          )}
        </Field>
        <Field label="Note" markOptional>
          {(props) => (
            <Input
              {...props}
              value={note}
              maxLength={500}
              onChange={(event) => setNote(event.target.value)}
            />
          )}
        </Field>
        <div>
          <SaveButton size="sm" pending={record.isPending} saved={form.justSaved} disabled={!ready}>
            Record bill
          </SaveButton>
        </div>
      </form>
    </Panel>
  );
}

function Bills({ data }: { data: UnitCosts }) {
  return (
    <Panel className="lg:col-span-2">
      <PanelHeader
        title="Bills"
        subtitle="Newest first. A replaced bill stays listed, so a correction shows what it replaced."
      />
      {data.bills.length === 0 ? (
        <p className="px-5 pb-5 text-[0.8125rem] text-ink-muted">
          No bills recorded for this month. The cost per property reads zero until they are.
        </p>
      ) : (
        <ul className="divide-y divide-rule border-t border-rule">
          {data.bills.map((bill) => (
            <li
              key={bill.id}
              className={`flex items-baseline justify-between gap-4 px-5 py-3 ${
                bill.inForce ? '' : 'text-ink-subtle'
              }`}
            >
              <div className="flex flex-col">
                <span className="text-[0.8125rem] font-semibold">
                  {bill.service} {bill.inForce ? null : <Chip tone="neutral">Replaced</Chip>}
                </span>
                <span className="text-[0.75rem] text-ink-subtle">
                  {COST_DRIVER_LABELS[bill.driver]}
                  {bill.note ? ` · ${bill.note}` : ''}
                </span>
              </div>
              <Amount
                value={BigInt(bill.amountCents)}
                currency
                className={`text-[0.8125rem] ${bill.inForce ? 'font-semibold' : 'line-through'}`}
              />
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

/**
 * Agree what an agency is charged, from a month.
 *
 * NEVER AN EDIT, and the form says so: recording a price for an agency that already has one is a
 * change effective from the month shown, leaving what was charged before exactly as it was. That is
 * what keeps a margin quoted last quarter reproducible.
 */
function SetRate({ month, data }: { month: string; data: UnitCosts }) {
  const form = useUnsavedChanges();
  const client = useQueryClient();
  const [agencyOrgId, setAgencyOrgId] = useState('');
  const [price, setPrice] = useState('');
  const [minimum, setMinimum] = useState('');
  const [note, setNote] = useState('');

  // Blank means none rather than nothing typed: a minimum is optional, and a price of zero is a
  // real answer for a pilot agency.
  const parse = (value: string): { cents: Money | null; issue: string | null } => {
    if (value.trim() === '') return { cents: 0n, issue: null };
    try {
      return { cents: parseAmount(value), issue: null };
    } catch {
      return { cents: null, issue: 'Enter the amount in dollars and cents' };
    }
  };
  const priced = parse(price);
  const floor = parse(minimum);
  const issues =
    priced.cents === null || floor.cents === null
      ? []
      : agencyRateIssues({ pricePerPropertyCents: priced.cents, minimumCents: floor.cents });
  const ready =
    agencyOrgId !== '' && priced.cents !== null && floor.cents !== null && issues.length === 0;

  const save = useMutation(
    trpc.platform.setAgencyRate.mutationOptions({
      meta: { errorTitle: 'The price was not recorded' },
      onSuccess: async () => {
        form.saved();
        setPrice('');
        setMinimum('');
        setNote('');
        await client.invalidateQueries({ queryKey: trpc.platform.unitCosts.queryKey() });
      },
    }),
  );

  const current = data.agencies.find((agency) => agency.orgId === agencyOrgId);

  return (
    <Panel>
      <PanelHeader
        title="Agree a price"
        subtitle={`What an agency is charged from ${formatPeriod(month)}. Ex-GST.`}
      />
      <form
        onChange={form.touch}
        className="flex flex-col gap-3 px-5 pb-5"
        onSubmit={(event) => {
          event.preventDefault();
          if (!ready || priced.cents === null || floor.cents === null) return;
          save.mutate({
            agencyOrgId,
            effectiveFrom: month,
            pricePerPropertyCents: priced.cents.toString(),
            minimumCents: floor.cents.toString(),
            note: note.trim() === '' ? null : note.trim(),
          });
        }}
      >
        <Field label="Agency">
          {(props) => (
            <Select
              {...props}
              value={agencyOrgId}
              onChange={(event) => setAgencyOrgId(event.target.value)}
            >
              <option value="">Choose an agency…</option>
              {data.agencies.map((agency) => (
                <option key={agency.orgId} value={agency.orgId}>
                  {agency.name}
                  {agency.rate === null ? ' — not priced' : ''}
                </option>
              ))}
            </Select>
          )}
        </Field>
        {current?.rate ? (
          <p className="text-[0.75rem] text-ink-subtle">
            On <Amount value={BigInt(current.rate.pricePerPropertyCents)} tone="neutral" /> per
            property since {formatPeriod(current.rate.effectiveFrom)}
            {BigInt(current.rate.minimumCents) > 0n ? (
              <>
                , <Amount value={BigInt(current.rate.minimumCents)} tone="neutral" /> minimum
              </>
            ) : null}
            . Recording a price changes it from {formatPeriod(month)}; earlier months keep theirs.
          </p>
        ) : null}
        <Field label="Price per property (NZD)" error={priced.issue ?? issues[0]}>
          {(props) => (
            <Input
              {...props}
              inputMode="decimal"
              value={price}
              onChange={(event) => setPrice(event.target.value)}
            />
          )}
        </Field>
        <Field
          label="Monthly minimum (NZD)"
          hint="What they pay when the per-property figure comes to less. Blank for none."
          error={floor.issue ?? undefined}
          markOptional
        >
          {(props) => (
            <Input
              {...props}
              inputMode="decimal"
              value={minimum}
              onChange={(event) => setMinimum(event.target.value)}
            />
          )}
        </Field>
        <Field label="Note" markOptional>
          {(props) => (
            <Input
              {...props}
              value={note}
              maxLength={500}
              onChange={(event) => setNote(event.target.value)}
            />
          )}
        </Field>
        <div>
          <SaveButton size="sm" pending={save.isPending} saved={form.justSaved} disabled={!ready}>
            Agree price
          </SaveButton>
        </div>
      </form>
    </Panel>
  );
}

function Agencies({ data }: { data: UnitCosts }) {
  return (
    <Panel className="lg:col-span-2">
      <PanelHeader
        title="Agencies"
        subtitle="Each agency's share of the bills, and how much of its statement matched itself."
      />
      {data.agencies.length === 0 ? (
        <p className="px-5 pb-5 text-[0.8125rem] text-ink-muted">No agencies on ProManage yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[52rem] border-collapse text-[0.8125rem]">
            <thead>
              <tr className="border-t border-rule text-left text-[0.6875rem] tracking-wide text-ink-subtle uppercase">
                <th className="px-5 py-2 font-semibold">Agency</th>
                <th className="px-5 py-2 text-right font-semibold">Properties</th>
                <th className="px-5 py-2 text-right font-semibold">Stored</th>
                <th className="px-5 py-2 text-right font-semibold">Emails</th>
                <th className="px-5 py-2 text-right font-semibold">Charged</th>
                <th className="px-5 py-2 text-right font-semibold">Cost</th>
                <th className="px-5 py-2 text-right font-semibold">Margin</th>
                <th className="px-5 py-2 text-right font-semibold">Auto-matched</th>
              </tr>
            </thead>
            <tbody className="tabular-nums">
              {data.agencies.map((agency) => (
                <tr key={agency.orgId} className="border-t border-rule align-top">
                  <td className="px-5 py-3 font-semibold">{agency.name}</td>
                  <td className="px-5 py-3 text-right">{agency.properties}</td>
                  <td className="px-5 py-3 text-right">
                    {formatBytes(BigInt(agency.storedBytes))}
                  </td>
                  <td className="px-5 py-3 text-right">{agency.emailsSent}</td>
                  <td className="px-5 py-3 text-right">
                    {agency.rate === null ? (
                      <Chip tone="neutral">Not priced</Chip>
                    ) : (
                      <>
                        <Amount value={BigInt(agency.revenueCents)} currency />
                        <div className="text-[0.75rem] text-ink-subtle">
                          {agency.rate.onMinimum ? (
                            <>
                              <Amount value={BigInt(agency.rate.minimumCents)} tone="neutral" />{' '}
                              minimum
                            </>
                          ) : (
                            <>
                              <Amount
                                value={BigInt(agency.rate.pricePerPropertyCents)}
                                tone="neutral"
                              />{' '}
                              each
                            </>
                          )}
                        </div>
                      </>
                    )}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <Amount value={BigInt(agency.costCents)} currency />
                    <div className="text-[0.75rem] text-ink-subtle">
                      {agency.perPropertyCents === null ? (
                        '—'
                      ) : (
                        <>
                          <Amount value={BigInt(agency.perPropertyCents)} tone="neutral" /> each
                        </>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-3 text-right">
                    {agency.rate === null ? (
                      '—'
                    ) : (
                      <>
                        <Amount
                          value={BigInt(agency.marginCents)}
                          currency
                          tone={BigInt(agency.marginCents) >= 0n ? 'positive' : 'warn'}
                        />
                        <div className="text-[0.75rem] text-ink-subtle">
                          {formatBasisPointsPercent(agency.marginBp)}
                        </div>
                      </>
                    )}
                  </td>
                  <td className="px-5 py-3 text-right">
                    {formatBasisPointsPercent(agency.autoRateBp)}
                    <div className="text-[0.75rem] text-ink-subtle">
                      {agency.lines} lines
                      {agency.autoWithdrawn > 0 ? ` · ${String(agency.autoWithdrawn)} undone` : ''}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}
